/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SearchClient } from 'algoliasearch/lite'
import classNames from 'classnames'
import type { Hit, BaseHit, UiState } from 'instantsearch.js'
import { InsightsProps } from 'instantsearch.js/es/middlewares'
import { When } from 'react-if'
import { Configure, InstantSearch, SearchBox, Hits, useSearchBox, useInstantSearch } from 'react-instantsearch'
import type { IconProps } from 'react-instantsearch/dist/es/ui/SearchBox'
import { v4 } from 'uuid'
import { CircleCloseIcon } from '@/atoms/Icons/CircleCloseIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { MagnifyingGlassIcon } from '@/atoms/Icons/MagnifyingGlassIcon'
import { AsH3, TitleMD } from '@/atoms/Text'
import { OMNIBOX_INDEX_NAME } from '@/constants/search'
import { useIsDarkMode } from '@/contexts/ThemeContext'
import { EpisodeSearchHit } from '@/molecules/Search/EpisodeSearchHit'
import { PageSearchHit } from '@/molecules/Search/PageSearchHit'
import { ProjectSearchHit } from '@/molecules/Search/ProjectSearchHit'
import { OmniboxIndexRecord } from '@/search/types'
import { useLocale } from '@/utils/LocaleUtil'
import { getAnonymousId, useSafeTrack } from '@/utils/analytics'
import { logger } from '@/utils/logging'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { getAlgoliaSearchClient } from './algolia-search-client'

export interface OmniboxSearchInputProps {
  className?: string
}

interface StateChangeParams {
  uiState: UiState
  setUiState: (uiState: UiState | ((previousUiState: UiState) => UiState)) => void
}

let mostRecentSearchId = ''
let mostRecentSearchQuery = ''
let searchSessionId = ''

function buildSearchTrackingPayload(payload?: any) {
  return {
    indexName: OMNIBOX_INDEX_NAME,
    searchSessionId,
    searchId: mostRecentSearchId,
    searchQuery: mostRecentSearchQuery,
    ...payload,
  }
}

// https://www.algolia.com/doc/guides/building-search-ui/getting-started/react/
export const OmniboxSearchInput: React.FC<OmniboxSearchInputProps> = (props) => {
  const [client, setClient] = useState<SearchClient | null>(null)
  const track = useSafeTrack()
  // Search values are not translated yet. In a future iteration of search that includes translations, open up locales as appropriate.
  const { locale } = useLocale()

  const handleStateChange = useCallback(
    ({ uiState, setUiState }: StateChangeParams) => {
      const index = uiState[OMNIBOX_INDEX_NAME]

      mostRecentSearchId = `${v4()}-${v4()}`
      mostRecentSearchQuery = index?.query ?? ''

      const payload = buildSearchTrackingPayload({
        configuration: index?.configure,
        query: index?.query,
        fullUiState: uiState,
      })
      track('Search Query Updated', payload)

      setUiState(uiState)
    },
    [track],
  )

  useEffect(() => {
    if (client) return
    else getAlgoliaSearchClient().then((client) => setClient(client))
  }, [client])

  if (locale !== 'en') return null
  if (!client) return null

  const anonId = getAnonymousId()

  /**
   * Docs for insights object: https://www.algolia.com/doc/api-reference/widgets/insights/react/#options
   */
  const insights: InsightsProps = {
    // Note: window.aa is the Aloglia analytics client.
    insightsClient: (window as any).aa,
    onEvent: (event, client: any) => {
      const basePayload = {
        hits: event.hits,
        fullEvent: event,
      }

      //TODO when our own events are fully flushed out, including dashboards, we can stop sending to Algolia
      if (event.insightsMethod && event.payload && client) {
        client(event.insightsMethod, event.payload)
      }

      if (event.insightsMethod === 'viewedObjectIDs') {
        const payload = buildSearchTrackingPayload(basePayload)
        // TODO for some reason the "Search Results Viewed" event is firing twice after most searches.
        // Need to figure out why so that we don't artificially inflate the analytics.
        track('Search Results Viewed', payload)
      } else if (event.insightsMethod === 'clickedObjectIDsAfterSearch') {
        const payload = buildSearchTrackingPayload({
          ...basePayload,
          position: (event.payload as any)?.positions[0],
        })
        track('Search Result Clicked', payload)
      } else {
        logger().error('unknown search insights event:', event)
      }
    },
  }

  return (
    <InstantSearch
      indexName={OMNIBOX_INDEX_NAME}
      searchClient={client}
      insights={insights}
      onStateChange={handleStateChange}
    >
      <Configure
        advancedSyntax={true}
        hitsPerPage={5}
        minWordSizefor1Typo={3}
        minWordSizefor2Typos={6}
        clickAnalytics={true}
        userToken={anonId}
      />
      <SearchInput {...props} />
    </InstantSearch>
  )
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const SearchInput: React.FC<OmniboxSearchInputProps> = ({ className }) => {
  const [isActive, setIsActive] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [shouldHideHits, setShouldHideHits] = useState(true)
  const mobileInputRef = useRef<HTMLDivElement | null>(null)
  const desktopInputRef = useRef<HTMLDivElement | null>(null)
  const { query, clear } = useSearchBox()
  const isDarkMode = useIsDarkMode()
  const track = useSafeTrack()
  const { t } = useTranslate('common')

  // See useInstanctSearch() hook docs here: https://www.algolia.com/doc/api-reference/widgets/use-instantsearch/react/
  const { results } = useInstantSearch()

  const focusMobileSearchInput = useCallback(() => {
    const searchWrapper = mobileInputRef?.current
    const searchDiv = searchWrapper?.children[0]
    const searchForm = searchDiv?.children[0]
    const searchInput = searchForm?.children[0] as HTMLInputElement | undefined
    searchInput?.focus()
  }, [mobileInputRef])

  const focusDesktopSearchInput = useCallback(() => {
    const searchWrapper = desktopInputRef?.current
    const searchDiv = searchWrapper?.children[0]
    const searchForm = searchDiv?.children[0]
    const searchInput = searchForm?.children[0] as HTMLInputElement | undefined
    searchInput?.focus()
  }, [desktopInputRef])

  const handleExpandClick = useCallback(() => {
    searchSessionId = `${v4()}-${v4()}`
    track('Search Icon Clicked', buildSearchTrackingPayload())
    setIsActive(true)
    focusMobileSearchInput()
    focusDesktopSearchInput()
  }, [setIsActive, focusMobileSearchInput, focusDesktopSearchInput, track])

  const handleMobileSearchIconClick = useCallback(() => {
    setIsMobileOpen(true)
    handleExpandClick()
  }, [handleExpandClick])

  const handleFocusLost = useCallback(() => {
    track('Search Abandoned', buildSearchTrackingPayload())
    clear()
    setIsActive(false)
  }, [clear, setIsActive, track])

  const handleMobileCloseSearch = useCallback(() => {
    setIsMobileOpen(false)
    handleFocusLost()
  }, [handleFocusLost])

  const handleHitClicked = useCallback(() => {
    clear()
    setIsActive(false)
    setIsMobileOpen(false)
  }, [clear])

  useEffect(() => {
    if (query) setShouldHideHits(false)
    else setShouldHideHits(true)
  }, [query, setShouldHideHits])

  useEffect(() => {
    if (!track || !results || !results.query) return

    const payload = buildSearchTrackingPayload({
      hits: results.hits,
      numHits: results.hits.length,
      query: results.query,
      fullResults: results,
    })
    track('Search Performed', payload)
    if (results.hits.length) {
      track('Search Performed With Results', payload)
    } else {
      track('Search Performed No Results', payload)
    }
  }, [results, track])

  // When mobile search is open, disable scrolling on the main page.
  // This prevents the page from scrolling behind the modal.
  useEffect(() => {
    const html = window.document.getElementsByTagName('html')[0]
    if (html) {
      if (isMobileOpen) {
        html.style.height = '100vh'
        html.style.overflow = 'hidden'
      } else {
        html.style.height = 'initial'
        html.style.overflow = 'initial'
      }
    }
  }, [isMobileOpen])

  return (
    <>
      {/* ----- Mobile / Tablet ----- */}
      <div className={classNames('flex flex-row items-center lg:hidden', className)}>
        <button
          aria-label={t('search', 'Search')}
          className={classNames(
            'transition-all duration-200',
            isMobileOpen ? 'opacity-0 w-0 h-0' : 'opacity-100 w-5 h-5',
          )}
          onClick={handleMobileSearchIconClick}
        >
          <MagnifyingGlassIcon size={20} color={isDarkMode ? 'white' : 'black'} />
        </button>
      </div>
      <div
        className={classNames(
          'absolute left-0 top-0 z-[10000] transition-height duration-200 w-screen lg:hidden',
          isDarkMode ? 'bg-core-gray-950' : 'bg-white',
          isMobileOpen ? 'h-screen overflow-scroll' : 'h-0 overflow-hidden',
        )}
      >
        <div className="mx-auto max-w-[760px] p-4">
          <div className="mt-4 flex flex-row items-center justify-between">
            <TitleMD as={AsH3} weight="semibold">
              <Translate t={t} i18nKey="search">
                Search
              </Translate>
            </TitleMD>
            <div role="button" aria-label={t('closeSearch', 'Close Search')} onClick={handleMobileCloseSearch}>
              <CloseIcon color={isDarkMode ? 'white' : 'black'} size={24} />
            </div>
          </div>
          <div className="mt-8 w-full" ref={mobileInputRef}>
            <SearchBox
              classNames={{
                root: classNames(
                  'rounded-full relative p-[1px] w-full',
                  isDarkMode
                    ? 'bg-black/50 backdrop-filter-blur-24px text-white'
                    : 'bg-core-gray-200 text-black placeholder:text-core-gray-950',
                ),
                form: classNames(
                  'pl-6 rounded-full border-[1px] border-transparent px-2 py-[5px]',
                  isDarkMode ? 'bg-core-gray-800 backdrop-filter-blur-24px' : 'bg-core-gray-200',
                ),
                input: classNames(
                  'clean-search-input w-[88%] h-4 outline-transparent focus:outline-transparent border-none focus:border-none mx-2 my-1 ring-0 focus:ring-0 ring-offset-0 focus:ring-offset-0 focus:ring-transparent',
                  isDarkMode ? 'bg-transparent backdrop-filter-blur-24px' : 'bg-core-gray-200',
                ),
              }}
              searchAsYouType={true}
              placeholder="Search"
              submitIconComponent={SubmitIcon}
              resetIconComponent={ResetIcon}
            />

            <div className="mt-2 w-full">
              <Hits
                classNames={{
                  root: classNames(
                    'w-full rounded-lg overflow-hidden',
                    isDarkMode ? 'bg-core-gray-950 shadow-dark-4' : 'bg-white shadow-light-4',
                  ),
                }}
                hidden={shouldHideHits}
                hitComponent={SearchHit}
                onClick={handleHitClicked}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ----- Desktop ----- */}
      <When condition={isActive}>
        <div
          className="absolute left-0 top-0 z-[9999] hidden h-screen w-screen bg-transparent lg:block"
          onClick={handleFocusLost}
        ></div>
      </When>
      <div className={classNames('z-[10000] flex-row items-center hidden lg:flex', className)}>
        <button
          aria-label={t('search', 'Search')}
          className={classNames('transition-all duration-300', isActive ? 'opacity-0 w-0 h-0' : 'opacity-100 w-5 h-5')}
          onClick={handleExpandClick}
        >
          <MagnifyingGlassIcon size={20} color={isDarkMode ? 'white' : 'black'} />
        </button>
        <div
          ref={desktopInputRef}
          className={classNames(
            'transition-width duration-300 overflow-hidden',
            isActive ? 'w-48 lg:w-[13rem] xl:w-[12rem] 3xl:w-[17rem]' : 'w-0',
          )}
        >
          <SearchBox
            classNames={{
              root: classNames(
                'rounded-full relative p-[1px] w-48 lg:w-[13rem] xl:w-[12rem] 3xl:w-[17rem]',
                isDarkMode
                  ? 'bg-black/50 backdrop-filter-blur-24px text-white'
                  : 'bg-core-gray-200 text-black placeholder:text-core-gray-950',
              ),
              form: classNames(
                'pl-6 rounded-[90px] border-[1px] border-transparent px-2 py-[5px]',
                isDarkMode ? 'bg-core-gray-800 backdrop-filter-blur-24px' : 'bg-core-gray-200',
              ),
              input: classNames(
                'clean-search-input w-[88%] h-4 outline-transparent focus:outline-transparent border-none focus:border-none mx-2 my-1 ring-0 focus:ring-0 ring-offset-0 focus:ring-offset-0 focus:ring-transparent',
                isDarkMode ? 'bg-transparent backdrop-filter-blur-24px' : 'bg-core-gray-200',
              ),
            }}
            searchAsYouType={true}
            placeholder="Search"
            submitIconComponent={SubmitIcon}
            resetIconComponent={ResetIcon}
          />

          <div className="absolute right-4 top-16">
            <Hits
              classNames={{
                root: classNames(
                  'w-full md:w-[40rem] rounded-lg overflow-hidden',
                  isDarkMode ? 'bg-core-gray-950 shadow-dark-4' : 'bg-white shadow-light-4',
                ),
              }}
              hidden={shouldHideHits}
              hitComponent={SearchHit}
              onClick={handleHitClicked}
            />
          </div>
        </div>
      </div>
    </>
  )
}

const SubmitIcon: React.JSXElementConstructor<IconProps> = () => {
  return <MagnifyingGlassIcon className="absolute left-2 top-[11px]" color="core-gray-600" size={16} />
}

const ResetIcon: React.JSXElementConstructor<IconProps> = () => {
  const isDarkMode = useIsDarkMode()

  return (
    <CircleCloseIcon
      className="absolute right-2 top-[7px]"
      color={isDarkMode ? 'core-gray-500' : 'core-gray-950'}
      size={24}
    />
  )
}

interface SearchHitProps {
  hit: Hit<BaseHit> & OmniboxIndexRecord
}

const SearchHit: React.JSXElementConstructor<SearchHitProps> = (props) => {
  const hit = props.hit

  if (hit.type === 'episode') return <EpisodeSearchHit hit={hit} />
  if (hit.type === 'project') return <ProjectSearchHit hit={hit} />
  if (hit.type === 'page') return <PageSearchHit hit={hit} />

  return null
}
